import React, { useEffect, useState, useCallback } from 'react';
import { connect } from 'react-redux';

// Components
import { PageWrapper } from 'src/universal/components/PageWrapper';
import StickyLeftRail from './components/StickyLeftRail';
import FeaturedBenefits from './components/FeaturedBenefits';
import ReinforcementBanner from './components/ReinforcementBanner';
import PznAboveTheFold from './components/PznAboveTheFold';
import { DynamicOfferBox } from './components/DynamicOfferBox';
import { AprBox } from './components/AprBox';
import BenefitsAccordion from './components/BenefitsAccordion';
import RABBanner from './components/RABBanner';
import XSellContainer from './components/XSell/Container';
import { StickyApplyBar } from './components/StickyApplyBar';
import { PerceptionOfCoverage } from './components/PerceptionOfCoverage';
import ApplyModal from './components/ApplyModal';
import Breadcrumbs from 'src/universal/components/Breadcrumbs';
import { FeeBox } from './components/FeeBox';
import DcfBanner from 'src/universal/components/DcfBanner';
import Hero from './components/Hero';
import DeltaBenefitsTiles from './components/DeltaBenefitsTiles';
import { FeaturedBenefitsTiles } from './components/BenefitsTileFeature';
import LazyScrollMetricTracker from 'src/universal/components/DTM/LazyScrollMetricTracker';
import HowYouEarn from './components/HowYouEarn';
import HowYouEarnMobileHeaderModal from './components/HowYouEarnMobileHeaderModal';
import BusinessValueTiles from './components/BusinessValueTiles';
import AprBoxBelowHowYouEarn from './components/AprBoxBelowHowYouEarn';
import { dispatchDtmActions } from 'src/universal/components/DispatchDtmActions';
import OgSpinner from 'src/universal/components/OfferGatingSpinner';
import ErrorPage from 'src/universal/components/ErrorPage';
import SuppCardsModal from 'src/universal/components/SuppModal';
import FeaturedBenefitsCarouselBox from './components/FeaturedBenefitsCarouselBox';
import DynamicAppModal from 'src/universal/components/DynamicApp/DynamicAppModal';
import TrackExperimentImpression from 'src/universal/components/TrackExperimentImpression';
import MobileVariantA from 'src/universal/components/Experiments/G170/Components/MobileVariantA';
import G170DesktopFilmstrip from 'src/universal/components/Experiments/G170/Components/DesktopVariant';
import G179Banner from 'src/universal/components/Experiments/G179';
import G175CmAddEmpBanner from 'src/universal/components/Experiments/G175';

// Selectors
import {
  getShopComponentsAnalyticsData,
  getPdPPznOfferEligibleDtm,
  getHeroDtmEvents,
  shouldShowDynamicAppPDP
} from './redux';
import { getOneXpExperiments } from 'src/universal/redux/oneXp.js';
import {
  getPageFirstCardData,
  getPageState,
  getIsSuppCardsModalOpen,
  getExperimentalPageData
} from 'src/universal/redux/page';
import { getDynAppModalContent } from 'src/universal/components/DynamicApp/redux';
import {
  getPznInfo,
  getPznSpecialOfferDtmInfo
} from 'src/universal/redux/pznData.js';
import { isCardMember, isLoggedInCardMember } from 'src/universal/redux/userType';
import {
  getMemberCardTitles,
  hasMemberInfoArrived
} from 'src/universal/redux/cardMemberInfo.js';
import needPznEligibleEvent from 'src/universal/utils/needPznEligibleEvent';
import { getOfferStylingPageLoadDtmList } from 'src/universal/utils/offerStyling';
import { doOfferStylingPgEvents } from 'src/universal/utils/offerStyling';
import { isG173Experiment } from 'src/universal/components/Experiments/G173/redux';
import { isG170MobileA } from 'src/universal/components/Experiments/G170/redux';
import { getCombinedG179Data } from 'src/universal/components/Experiments/G179/redux';

// Utils
import { isBrowser } from 'acq-utils/lib/misc';
import { InitFetchPageDataHelper } from 'src/universal/redux/utils/InitFetchPageDataHelper';
import OfferGatingForm from 'page_modules/utils/offer-gating';
import { suppModalState } from 'page_modules/business-pdp/utils/setModalStateAction';
import { getCardInfo } from 'service_modules/models/card-names/card-names.model';
import {
  pageLevelBundledCmPznExt,
  cmComponentPznExt
} from 'src/universal/utils/orchestraCmPznExtEvent';
import { getHostCardExtItem } from 'page_modules/business-pdp/utils/cmExtHelper';
import { drawerOpenCloseHandler } from 'src/universal/utils/accordionDrawerSupport';
import {
  DynamicAppModalContext,
  ModalButtonRefContext
} from 'src/universal/components/DynamicApp/redux';

// Hooks
import useTrackShopComponents from 'src/universal/hooks/useTrackShopComponents';
import trackShopComponentsByKeyword from 'src/universal/hooks/useTrackShopComponents/trackingFuntions/trackByKeyword';
import useDrawerEvents from 'src/universal/hooks/useAccordionDrawerEvents';

// Config
import { pznPageIds } from 'src/universal/components/PZN/config';

// Styles
import './__styles__/navs.scss';

// Vars/Enums/Etc
import { name as pageName, className } from './__styles__/pageVars.scss';

const FetchPageDataHelper = new InitFetchPageDataHelper({
  pageName,
  shouldDispatchDtmDataReady: false
});

/* eslint-disable complexity */
/*eslint max-statements: [2, 30]*/
const PageComponent = React.memo(function PageComponent({
  dispatch,
  pageState = {},
  cardData = {},
  params = {},
  shopComponentsAnalyticsData,
  pagePznId,
  pznInfo,
  cardMemberState,
  cardMemberTitles,
  isUserLoggedIn,
  pznData,
  hasMemberInfo,
  suppModalOpen,
  dtmEvents,
  pznEligibleDtm,
  pznSpecialOfferDtmInfo,
  offerStylingPgLoadDtms,
  dynAppModalContent,
  shouldShowDynamicApp,
  isG173,
  G170MobileA,
  xpData,
  g179Info
}) {
  useDrawerEvents('main-container', isDrawerOpen => {
    drawerOpenCloseHandler(isDrawerOpen);
  });
  const { scrollToLeftRailArt } = dtmEvents;
  const cardShortName = (getCardInfo(params.cardName) || {}).shortName;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [buttonRef, setButtonRef] = useState(null);
  const dynAppButtonRef = { buttonRef, setButtonRef };
  const toggleDynamicAppModalOpen = useCallback(
    () => setIsModalOpen(prev => !prev),
    []
  );

  useTrackShopComponents({
    trackingFn: trackShopComponentsByKeyword,
    analyticsData: shopComponentsAnalyticsData
  });

  dispatchDtmActions({
    FetchPageDataHelper,
    dispatch,
    data: pageState,
    cardShortName,
    dtmCategoryPaths: [
      'cards[0].dtmProductPageName',
      'page.dtm.subCategory2',
      'page.dtm.subCategory3'
    ],
    isCardMember: cardMemberState,
    cardTitles: cardMemberTitles,
    isUserLoggedIn,
    hasCMInfoArrived: hasMemberInfo,
    usePZN: true,
    pznData
  });

  const toggleModal = e => {
    e?.stopPropagation();
    dispatch(suppModalState(!suppModalOpen));
  };

  useEffect(
    () => {
      setTimeout(() => {
        cmComponentPznExt(getHostCardExtItem(pznData?.pznOfferList));
        pageLevelBundledCmPznExt();
      }, 200);
    },
    [hasMemberInfo, pznData?.pznOfferList]
  );

  if (isBrowser() && pznInfo.hasError) {
    return (
      <ErrorPage
        httpStatus={pznInfo?.errorInfo?.errorCode}
        reqUrl={pznInfo?.errorInfo?.reqUrl}
      />
    );
  }

  const isGoldCard = cardShortName === 'gold';
  const isPlatinumCard = cardShortName === 'platinum';
  const isDeltaReserve = cardShortName === 'delta-reserve';
  const isDeltaGold = cardShortName === 'gold-delta-skymiles';
  const isDeltaPlat = cardShortName === 'platinum-delta-skymiles';
  const isHiltonHonors = cardShortName === 'hilton-honors';
  const isDeltaPlatOrGold = isDeltaGold || isDeltaPlat;
  const isRenderingHero = isGoldCard || isPlatinumCard || isDeltaReserve;
  const altBPlatBenefitsHierarchy = isPlatinumCard && isG173;

  let isG179 = false;

  if (g179Info?.oneXpDecided === true && g179Info?.isAffiliatePgMode) {
    isG179 = true;
  }

  let showXSell = !isPlatinumCard;

  if (isG179) {
    showXSell = false;
  }

  return (
    <PageWrapper
      shouldRenderOfferExpiry
      displayName={className}
      FetchPageDataHelper={FetchPageDataHelper}
      data={pageState}
      usePZN={true}
      cardShortName={cardShortName}
      pznPageId={pagePznId}
      seoData={cardData.seo}
      pmcCode={cardData.pmcCode}
      mainContainerProps={{ hideSubNav: isG179 }}
    >
      <DynamicAppModalContext.Provider value={toggleDynamicAppModalOpen}>
        <ModalButtonRefContext.Provider value={dynAppButtonRef}>
          <TrackExperimentImpression experimentName="GCS-147-DynamicApp" />
          <TrackExperimentImpression experimentName="GCS-154-Metal-Card" />
          <TrackExperimentImpression experimentName="GCS-152-PDP-Affiliate-Badge" />
          <TrackExperimentImpression experimentName="GCS160-LTO-Badge" />
          <TrackExperimentImpression experimentName="GCS-157-PDP-Mobile-Horizontal-Scroll" />
          <TrackExperimentImpression experimentName="GCS-171-ApplyNow-LockIcon-Removal" />
          <TrackExperimentImpression experimentName="GCS-173-BPlat-PDP-Benefits-Hierarchy" />
          <TrackExperimentImpression experimentName="GCS-170-PDP-Filmstrip-Mobile" />
          <TrackExperimentImpression experimentName="GCS-163-BPlat-PDP-Seamless-Campaign" />
          <TrackExperimentImpression experimentName="GCS-170-PDP-Filmstrip-Desktop" />
          <TrackExperimentImpression experimentName="GCS-179-PDP-Affiliate-Journey" />
          <TrackExperimentImpression experimentName="GCS-175-PDP-CM-AddEmp-Banner" />
          <LazyScrollMetricTracker
            callBackFunction={() => {
              doOfferStylingPgEvents(offerStylingPgLoadDtms);
            }}
            startTrackingAfterPznArrival={true}
          />
          <HowYouEarnMobileHeaderModal />
          <OfferGatingForm />
          {isG179 && <G179Banner />}
          {!isG179 &&
            G170MobileA && (
              <MobileVariantA
                data={xpData?.g170SubnavData}
                shortName={cardShortName}
              />
            )}
          {!isG179 && <G170DesktopFilmstrip />}
          {shouldShowDynamicApp && (
            <DynamicAppModal
              modal={isModalOpen}
              toggle={toggleDynamicAppModalOpen}
              content={dynAppModalContent}
              buttonRef={dynAppButtonRef.buttonRef}
            />
          )}
          {(pznSpecialOfferDtmInfo || {}).needEvent && (
            <LazyScrollMetricTracker
              scrollDtm={pznSpecialOfferDtmInfo.dtmObj}
              startTrackingAfterPznArrival={true}
            />
          )}
          <Hero shortName={cardShortName} />
          <ApplyModal />
          {!isG179 && <Breadcrumbs orientation="tablet" />}

          {needPznEligibleEvent(
            pznData?.pznOfferList,
            (cardData || {}).shortName
          ) && (
            <LazyScrollMetricTracker
              scrollDtm={pznEligibleDtm}
              offset={200}
              startTrackingAfterPznArrival={true}
            />
          )}
          <StickyLeftRail pageDispatch={dispatch}>
            {isRenderingHero && (
              <LazyScrollMetricTracker
                scrollDtm={scrollToLeftRailArt}
                offset={-80}
              />
            )}

            {!isG179 && <Breadcrumbs orientation="desktop" />}

            {!isRenderingHero ? (
              <PznAboveTheFold spinnerHeight="400px">
                <DynamicOfferBox shortName={cardShortName} />
                <RABBanner />
                {!isDeltaPlatOrGold && !isHiltonHonors && <AprBox />}
              </PznAboveTheFold>
            ) : !isGoldCard && !isPlatinumCard ? (
              <RABBanner />
            ) : null}
            <FeeBox />
            {isGoldCard && <RABBanner />}
            {isHiltonHonors && <HowYouEarn />}
            {isHiltonHonors && <AprBox />}
            {!isHiltonHonors && <HowYouEarn />}
            <G175CmAddEmpBanner />
            <DeltaBenefitsTiles location="top" />
            <DeltaBenefitsTiles location="bottom" />
            {altBPlatBenefitsHierarchy ? (
              <FeaturedBenefitsTiles />
            ) : (
              <BusinessValueTiles />
            )}
            {(isGoldCard || isDeltaReserve || isDeltaPlatOrGold) && <AprBox />}
            {altBPlatBenefitsHierarchy ? (
              <BusinessValueTiles />
            ) : (
              <FeaturedBenefitsTiles />
            )}
            <FeaturedBenefits cardName={cardShortName} />
            {isPlatinumCard && <AprBoxBelowHowYouEarn />}
            <PerceptionOfCoverage />
            <FeaturedBenefitsCarouselBox />
            <BenefitsAccordion />
            {isPlatinumCard && <RABBanner />}
          </StickyLeftRail>
          <ReinforcementBanner />
          <SuppCardsModal showSuppCardsModal={suppModalOpen} toggle={toggleModal} />
          {showXSell && <XSellContainer />}
          <StickyApplyBar />
          <DcfBanner pageType="business" pageId={pznPageIds.businessPDP} />
          <OgSpinner />
        </ModalButtonRefContext.Provider>
      </DynamicAppModalContext.Provider>
    </PageWrapper>
  );
});

FetchPageDataHelper.pageDataSetup(PageComponent, className);

const mapStateToProps = state => {
  const cardObj = getPageFirstCardData(state);
  return {
    pageState: getPageState(state),
    cardData: cardObj,
    shopComponentsAnalyticsData: getShopComponentsAnalyticsData(state),
    pagePznId: pznPageIds[pageName],
    pznInfo: getPznInfo(state),
    cardMemberState: isCardMember(state),
    cardMemberTitles: getMemberCardTitles(state),
    isUserLoggedIn: isLoggedInCardMember(state),
    pznData: state.pznData,
    hasMemberInfo: hasMemberInfoArrived(state),
    suppModalOpen: getIsSuppCardsModalOpen(state),
    G148: getOneXpExperiments(state)['GCS-148-AA-TEST'],
    dtmEvents: getHeroDtmEvents(state),
    pznEligibleDtm: getPdPPznOfferEligibleDtm(state),
    pznSpecialOfferDtmInfo: getPznSpecialOfferDtmInfo(state),
    G149Desktop: getOneXpExperiments(state)['GCS-149-Desktop'],
    G149Mobile: getOneXpExperiments(state)['GCS-149-Mobile'],
    offerStylingPgLoadDtms: getOfferStylingPageLoadDtmList(state, [
      cardObj.shortName
    ]),
    dynAppModalContent: getDynAppModalContent(state),
    shouldShowDynamicApp: shouldShowDynamicAppPDP(state),
    isG173: isG173Experiment(state),
    G170MobileA: isG170MobileA(state),
    xpData: getExperimentalPageData(state),
    g179Info: getCombinedG179Data(state)
  };
};

export default connect(mapStateToProps)(PageComponent);
